import axios from 'axios';
import { supabase } from '../lib/supabase';

// Use environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and debugging
api.interceptors.request.use(
  async (config) => {
    try {
      // In development, check if we should use bypass token
      if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && 
          localStorage.getItem('useDevBypass') === 'true') {
        config.headers.Authorization = 'Bearer dev-bypass-token';
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[Dev Bypass]');
        return config;
      }
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.access_token) {
        // Add authorization header
        config.headers.Authorization = `Bearer ${session.access_token}`;
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[Authenticated]');
      } else {
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[No Auth]');
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
      // In development, use bypass token on error
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.warn('⚠️  Auth error - using dev bypass token');
        config.headers.Authorization = 'Bearer dev-bypass-token';
        localStorage.setItem('useDevBypass', 'true');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Got 401, attempting to refresh token...');
      originalRequest._retry = true;
      
      try {
        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !session) {
          console.error('Token refresh failed:', refreshError);
          
          // In development, try using bypass token before redirecting
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.warn('⚠️  DEVELOPMENT: Using bypass token due to auth issues');
            localStorage.setItem('useDevBypass', 'true');
            originalRequest.headers.Authorization = 'Bearer dev-bypass-token';
            originalRequest._retryWithBypass = true;
            return api(originalRequest);
          }
          
          // Redirect to login if refresh fails
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Update the authorization header and retry
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        console.log('Token refreshed, retrying request...');
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        
        // In development, try using bypass token before redirecting
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.warn('⚠️  DEVELOPMENT: Using bypass token due to auth error');
          localStorage.setItem('useDevBypass', 'true');
          originalRequest.headers.Authorization = 'Bearer dev-bypass-token';
          originalRequest._retryWithBypass = true;
          return api(originalRequest);
        }
        
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Health check
export const checkHealth = async () => {
  const response = await api.get('/api/health');
  return response.data;
};